import React, { useEffect, useState } from "react";
import { Mail, Pencil, Clock } from "lucide-react";
import GuestMails from "@/components/mailbox/GuestMails";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { CompletionBadge, ExpiredLinkFlag } from "@/pages/shared";
import ReservationForm, { reservationFromRow, missingDefinitive, blockingReasons } from "@/components/ReservationForm";

export function useBookingData() {
  const [guests, setGuests] = useState([]);
  const [zones, setZones] = useState([]);
  const [pitches, setPitches] = useState([]);
  const load = async () => {
    const [g, z, p] = await Promise.all([api.get("/guests"), api.get("/zones"), api.get("/pitches")]);
    setGuests(g.data); setZones(z.data); setPitches(p.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);
  return { guests, zones, pitches, reloadBooking: load };
}

export default function EditReservationDialog({ reservation, onClose, onSaved, guests, zones, pitches, withDocuments = false, arrivalToday = false }) {
  const { t } = useLang();
  const [form, setForm] = useState(null);
  const [showMissing, setShowMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showMails, setShowMails] = useState(false);
  const [expOverride, setExpOverride] = useState(null);

  useEffect(() => { setShowMails(false); setExpOverride(null); }, [reservation]);
  useEffect(() => {
    if (!reservation) { setForm(null); setShowMissing(false); return; }
    const f = reservationFromRow(reservation, guests);
    if (arrivalToday) {
      // Bij een (late) aankomst: aankomst standaard op vandaag (blijft aanpasbaar);
      // ligt vertrek vóór vandaag, dan vertrek standaard op vandaag + 1 nacht.
      const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const today = iso(new Date());
      const tomorrow = iso(new Date(Date.now() + 86400000));
      if (!f.arrival || f.arrival < today) f.arrival = today;
      if (!f.departure || f.departure < today) f.departure = tomorrow;
      if (f.departure <= f.arrival) f.departure = iso(new Date(new Date(f.arrival).getTime() + 86400000));
    }
    setForm(f);
    setShowMissing(false);
  }, [reservation, guests, arrivalToday]);
  if (!reservation || !form) return null;

  const save = async (definitive) => {
    // Geef bij elke blokkade duidelijke uitleg i.p.v. een knop die stil uitgeschakeld blijft.
    const reasons = blockingReasons(form, t);
    if (reasons.length) { setShowMissing(true); toast.error(`${t("missing_fields")}: ${reasons.join(", ")}`); return; }
    if (definitive && missingDefinitive(form).length) { setShowMissing(true); toast.error(t("missing_fields")); return; }
    setBusy(true);
    try {
      const { documents, ...rest } = form;
      await api.put(`/reservations/${reservation.id}`, { ...rest, status: reservation.status, definitive, meter_start: rest.meter_start === "" || rest.meter_start == null ? null : Number(rest.meter_start) });
      toast.success(t("saved"));
      onSaved && (await onSaved());
      onClose();
    } catch (e) { const d = e.response?.data?.detail; toast.error(d?.msg || apiError(d)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!reservation} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[96vw] xl:max-w-7xl max-h-[94vh] overflow-y-auto cb-scroll" data-testid="edit-reservation-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 flex-wrap">
            <Pencil className="h-4 w-4" />{t("edit")} — {reservation.guest_name} · {reservation.pitch_label}
            <CompletionBadge definitive={reservation.definitive} testid="edit-completion-badge" />
            <ExpiredLinkFlag reservation={expOverride ? { ...reservation, complete_link_exp: expOverride } : reservation} testid="edit-link-expired" />
            {reservation.definitive === false && (
              <Button type="button" size="sm" variant="outline" data-testid="edit-extend-complete-link"
                onClick={() => api.post(`/reservations/${reservation.id}/extend-complete-link`).then(({ data }) => { toast.success(`${t("complete_link_extended")} ${data.to}`); setExpOverride(data.expires); onSaved && onSaved(); }).catch((e) => toast.error(apiError(e.response?.data?.detail)))}>
                <Clock className="h-4 w-4 mr-1" />{t("extend_complete_link")}
              </Button>
            )}
            <Button type="button" size="sm" variant={showMails ? "default" : "outline"} className="ml-auto" onClick={() => setShowMails((v) => !v)} data-testid="reservation-mails-toggle">
              <Mail className="h-4 w-4 mr-1" />{t("mb_emails")}
            </Button>
          </DialogTitle>
        </DialogHeader>
        {showMails && <div className="mb-3" data-testid="reservation-mails"><GuestMails email={form.booker?.email} onNavigate={onClose} /></div>}
        <ReservationForm stayApproved withOccupants form={form} setForm={setForm} guests={guests} zones={zones} pitches={pitches} highlightMissing={showMissing} withDocuments={withDocuments} />
        <DialogFooter className="sm:justify-between items-center gap-3">
          <p className="text-[11px] text-muted-foreground max-w-md">{t("provisional_hint")} {t("definitive_hint")}</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
            {reservation.status === "reserved" && <Button variant="secondary" disabled={busy} onClick={() => save(false)} data-testid="edit-save-provisional">{t("save_provisional")}</Button>}
            <Button disabled={busy} onClick={() => save(true)} data-testid="edit-save-definitive">{reservation.status === "reserved" ? t("save_definitive") : t("save")}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
